import { lazy, type ComponentType } from "react";

import { FEATURES } from "types/landlord/partner";
import {
  withCrmAccessGate,
  withCrmFeatureGate
} from "shared/app/BoomCrmPaywall/withCrmFeatureGate";
import {
  useIsLeadsGated,
  useIsListingSettingsGated
} from "shared/app/BoomCrmPaywall/hooks";
import IntegrationsView from "views/settings-integration";
import { GoogleAnalyticsView } from "views/settings-integration/integrations/google-analytics";
import { GoogleAdsView } from "views/settings-integration/integrations/google-ads";
import { HubSpotView } from "views/settings-integration/integrations/hubspot";
import { CodeboxView } from "views/settings-integration/integrations/codebox";
import { SentriLockView } from "views/settings-integration/integrations/sentrilock";
import { IgloohomeView } from "views/settings-integration/integrations/igloohome";
import { ZillowView } from "views/settings-integration/integrations/zillow";
import { ZumperView } from "views/settings-integration/integrations/zumper";
import { DwellsyView } from "views/settings-integration/integrations/dwellsy";
import { ApartmentsComView } from "views/settings-integration/integrations/apartments-com";
import { MHVillageView } from "views/settings-integration/integrations/mhvillage";
import { AffordableHousingComView } from "views/settings-integration/integrations/affordable-housing-com";
import { MetaAdsView } from "views/settings-integration/integrations/meta-ads";
import { BoomFinanceView } from "views/settings-integration/integrations/boom-finance";
import type { PartnerUserPermissions } from "providers/Auth";

import type { RouteConfig } from "../types";

const SettingsView = lazy(() => import("views/settings"));
const Profile = lazy(() => import("views/settings-profile"));
const ApiView = lazy(() => import("views/settings-api"));
const PmsView = lazy(
  () => import("views/settings-integration/integrations/pms")
);
const PmsIntegrationView = lazy(
  () => import("views/settings-integration/integrations/pms-integration")
);
const UsersView = lazy(() => import("views/settings-users"));
const UserPermissionsView = lazy(() => import("views/settings-users-id"));
const RoleDetailView = lazy(() => import("views/settings-roles-id"));
const AgentDetailView = lazy(() => import("views/settings-agents-id"));
const LeasingAgentDetailView = lazy(
  () => import("views/settings-leasing-agents-id")
);
const AddAgentView = lazy(() => import("views/settings-users-add-agent"));
const TrainingContextView = lazy(
  () => import("views/settings-users-training-context")
);
const NotificationsView = lazy(() => import("views/settings-notifications"));
const FeaturesView = lazy(() => import("views/settings-features"));
const SdkFeaturesView = lazy(() => import("views/settings-sdk-features"));
const ApplicationView = lazy(() => import("views/settings-application"));
const CommunicationView = lazy(() => import("views/settings-communication"));
const EmailTemplateFormView = lazy(() => import("views/email-template-form"));
const SettingsCRMView = lazy(() => import("views/settings-crm"));
// Contact forms only exist to mint leads: with no lead entitlement the builder
// paywalls itself, like the Contact forms tab it is reached from (CRM-759).
const ContactFormBuilderView = withCrmAccessGate(
  useIsLeadsGated,
  "leads",
  lazy(() => import("views/contact-form-builder"))
);
const BillingView = lazy(() => import("views/billing"));
const BillingConfigurationView = lazy(
  () => import("views/settings-billing-configuration")
);
const StatementsIdView = lazy(() => import("views/statements-id"));
const BillingServicePeriodView = lazy(
  () => import("views/settings-billing-service-period")
);
const UpcomingStatementView = lazy(() => import("views/upcoming-statement"));
// The showings settings card stays visible for the whole CRM ecosystem, so all
// four pages behind it paywall themselves rather than 404 (CRM-759). Their own
// endpoints answer 403 without the entitlement, so an ungated page would render
// and immediately error-toast.
const gateShowings = (Component: ComponentType) =>
  withCrmFeatureGate(FEATURES.SHOWINGS, "showings", Component);
// The six listing-feed integration pages ride the `syndication` entitlement
// (CRM-908): reachable from the Add integration list even when locked, so they
// paywall themselves rather than render and 403.
const gateSyndication = (Component: ComponentType) =>
  withCrmFeatureGate(FEATURES.SYNDICATION, "syndication", Component);

const SettingsShowingView = gateShowings(
  lazy(() => import("views/settings-showings"))
);
const SettingsShowingsAccountView = gateShowings(
  lazy(() => import("views/settings-showings-account"))
);
const SettingsShowingsPersonalView = gateShowings(
  lazy(() => import("views/settings-showings-personal"))
);
const SettingsShowingsPersonalPreviewView = gateShowings(
  lazy(() => import("views/settings-showings-personal-preview"))
);
const ApplicantExperienceView = lazy(
  () => import("views/settings-applicant-experience")
);
// Settings → Listings page has no entitlement of its own in the CRM-908 spec;
// until product decides it follows Listing settings, i.e. showings (CRM-759 QA).
// The card stays and the page paywalls itself.
const SettingsListingsPageView = withCrmAccessGate(
  useIsListingSettingsGated,
  "listingsPage",
  lazy(() => import("views/settings-listings-page"))
);

type PartnerType = {
  isBoomScreen: boolean;
  isBoomCRM: boolean;
  isSDK: boolean;
  adminPrivileges: boolean;
  isCommunicationEmail: boolean;
};

export const getSettingsRoutes = (
  type: PartnerType,
  permissions: PartnerUserPermissions
): RouteConfig[] => [
  { path: "", index: true, component: SettingsView },

  // Admin
  { path: "api", component: ApiView, when: type.adminPrivileges },
  {
    path: "integration",
    when: type.adminPrivileges,
    children: [
      { path: "", index: true, component: IntegrationsView },
      { path: "pms", component: PmsView },
      { path: "pms/:integrationId", component: PmsIntegrationView },
      { path: "google-analytics", component: GoogleAnalyticsView },
      { path: "google-ads", component: GoogleAdsView },
      { path: "hubspot", component: HubSpotView },
      { path: "codebox", component: CodeboxView },
      { path: "sentrilock", component: SentriLockView },
      { path: "igloohome", component: IgloohomeView },
      { path: "zillow", component: gateSyndication(ZillowView) },
      { path: "zumper", component: gateSyndication(ZumperView) },
      { path: "dwellsy", component: gateSyndication(DwellsyView) },
      { path: "apartments-com", component: gateSyndication(ApartmentsComView) },
      { path: "mhvillage", component: gateSyndication(MHVillageView) },
      {
        path: "affordable-housing-com",
        component: gateSyndication(AffordableHousingComView)
      },
      { path: "meta-ads", component: MetaAdsView, when: type.isBoomCRM },
      { path: "boom-finance", component: BoomFinanceView }
    ]
  },
  {
    path: "application",
    component: ApplicationView,
    when: type.adminPrivileges
  },
  { path: "features", component: FeaturesView, when: type.adminPrivileges },
  {
    path: "applicant-experience",
    component: ApplicantExperienceView,
    when: type.isBoomScreen && type.adminPrivileges
  },
  {
    path: "listings-page",
    component: SettingsListingsPageView,
    when: type.isBoomCRM && type.adminPrivileges
  },

  // Users & Roles
  {
    path: "users",
    component: UsersView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "users/:userId",
    component: UserPermissionsView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "roles/:roleId",
    component: RoleDetailView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "agents/:agentId",
    component: AgentDetailView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "users/add-agent/:agentId?",
    component: AddAgentView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "users/training-context/*",
    component: TrainingContextView,
    when: permissions.canAccessUsersSettings
  },
  {
    path: "leasing-agents/:agentId",
    component: LeasingAgentDetailView,
    when: permissions.canAccessUsersSettings
  },

  // Billing
  {
    path: "billing",
    component: BillingView,
    when: permissions.canAccessBillingSettings
  },
  {
    path: "billing/configuration",
    component: BillingConfigurationView,
    when: permissions.canAccessBillingSettings
  },
  {
    path: "billing/upcoming",
    component: UpcomingStatementView,
    when: permissions.canAccessBillingSettings
  },
  {
    path: "billing/statements/:statementId",
    component: StatementsIdView,
    when: permissions.canAccessBillingSettings
  },
  {
    path: "billing/service-periods/:servicePeriodDate",
    component: BillingServicePeriodView,
    when: permissions.canAccessBillingSettings
  },
  {
    path: "billing/service-periods/:servicePeriodDate/statements/:statementId",
    component: StatementsIdView,
    when: permissions.canAccessBillingSettings
  },

  // SDK
  { path: "sdk-features", component: SdkFeaturesView, when: type.isSDK },

  // Profile & Notifications
  { path: "profile", component: Profile },
  { path: "notifications", component: NotificationsView },

  // Showings
  {
    path: "showings",
    component: SettingsShowingView,
    when: type.isBoomCRM
  },
  {
    path: "showings/account",
    component: SettingsShowingsAccountView,
    when: type.isBoomCRM
  },
  {
    path: "showings/personal",
    component: SettingsShowingsPersonalView,
    when: type.isBoomCRM
  },
  {
    path: "showings/personal/preview",
    component: SettingsShowingsPersonalPreviewView,
    when: type.isBoomCRM
  },

  // CRM
  { path: "crm", component: SettingsCRMView, when: type.isBoomCRM },
  {
    path: "crm/contact-forms/new",
    component: ContactFormBuilderView,
    when: type.isBoomCRM
  },
  {
    path: "crm/contact-forms/:id/edit",
    component: ContactFormBuilderView,
    when: type.isBoomCRM
  },
  {
    path: "communication",
    component: CommunicationView,
    when: type.isBoomCRM
  },
  // Custom email templates are the email channel — no direct-URL access to the
  // editor without communication_email (CRM-1132).
  {
    path: "communication/email-templates/create",
    component: EmailTemplateFormView,
    when: type.isBoomCRM && type.isCommunicationEmail
  },
  {
    path: "communication/email-templates/:id/edit",
    component: EmailTemplateFormView,
    when: type.isBoomCRM && type.isCommunicationEmail
  }
];
